import { Combobox, Label, PageTabsContent } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { EditUserForm } from "./useEditUserForm";
import { NON_ADMIN_ROLE, useRolesQuery } from "@tellescope/tanstack-query";
import { useMemo } from "react";

export interface RoleTabContentProps {
  form: EditUserForm;
}

export const RoleTabContent = ({ form }: RoleTabContentProps) => {
  const { t } = useTranslation("settings");
  const { roles } = useRolesQuery({
    select: (roles) =>
      roles.map(({ role }) =>
        role === NON_ADMIN_ROLE ? { value: role, label: t("Non-Admin") } : { value: role, label: role }
      ),
  });

  const billingSeatOptions = [
    { value: "FULL_SEAT", label: t("Full Seat") },
    { value: "READ_ONLY_SEAT", label: t("Read Only Seat") },
    { value: "PART_TIME_SEAT", label: t("Part Time Seat") },
    { value: "SCHEDULING_SEAT", label: t("Scheduling Seat") },
    { value: "UNBILLED_SEAT", label: t("Unbilled Seat") },
  ];

  const selectedRoleOption = useMemo(
    () => roles?.find(({ value }) => value === form.values.roles),
    [form.values.roles, roles]
  );
  const selectedBillingSeatOption = billingSeatOptions.find((option) => option.value === form.values.accountType);

  return (
    <PageTabsContent
      value="role"
      className="px-6 py-4 space-y-4 overflow-y-auto"
      style={{
        maxHeight: "min(80vh, 900px)",
      }}
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="roles">{t("Role")}</Label>
          <Combobox
            id="roles"
            value={selectedRoleOption}
            onChange={(option) => form.setFieldValue("roles", option?.value ?? "")}
            options={roles}
            placeholder={t("No role assigned")}
            searchPlaceholder={t("Search roles...")}
            emptyText={t("No roles found")}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="accountType">{t("Billing Seat")}</Label>
          <Combobox
            id="accountType"
            value={selectedBillingSeatOption}
            onChange={(option) => form.setFieldValue("accountType", option?.value ?? "")}
            options={billingSeatOptions}
            placeholder={t("Select a billing seat")}
            searchPlaceholder={t("Search...")}
            emptyText={t("No options found")}
            clearable
          />
        </div>
      </div>
    </PageTabsContent>
  );
};
